import { ApiCaller } from "@virtual-brain-lib/frontend-api";
import { Button, Icon, Span } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";
import {
	type AnalyticsEventDomain,
	type Content,
	isSheetFile,
	isValidContentMimeType,
	OFFICE_MIME_TYPES,
	OUTLOOK_MSG_MIME_TYPE,
	PDF_MIME_TYPE,
	type SourceFile,
	TEXT_CSV_MIME_TYPE,
	TEXT_HTML_MIME_TYPE,
	TEXT_MARKDOWN_MIME_TYPE,
	TEXT_PLAIN_MIME_TYPE,
	type VectorType,
} from "@virtual-brain-lib/types";

import { useDisconnectedState } from "../disconnected-state";
import MarkdownInlineSourceTooltip from "./MarkdownInlineSourceTooltip";

interface MarkdownInlineSourceProps {
	readonly isLoading: boolean;
	readonly excerpt: string;
	readonly index: string;
	readonly content?: Content | undefined;
	readonly contentId?: string | undefined;
	readonly messageId?: string | undefined;
	readonly imageUrl?: string | undefined;
	readonly vectorId?: string | undefined;
	readonly type?: VectorType | "excel-workbook" | undefined;
	readonly disabled?: boolean | undefined;
	readonly page?: string | undefined;
	readonly onSourceSelected?: ((params: SourceFile) => void) | undefined;
	readonly isMarkdownSectionSelected?: boolean;
	readonly analyticsEventDomain?: AnalyticsEventDomain | undefined;
}

const VISIONEUSE_ACCEPTED_CONTENT_MIME_TYPES: readonly string[] = [
	TEXT_PLAIN_MIME_TYPE,
	TEXT_MARKDOWN_MIME_TYPE,
	TEXT_CSV_MIME_TYPE,
	TEXT_HTML_MIME_TYPE,
	OUTLOOK_MSG_MIME_TYPE,
	PDF_MIME_TYPE,
	...OFFICE_MIME_TYPES,
];

export default function MarkdownInlineSource({
	isLoading,
	content,
	contentId,
	vectorId,
	messageId,
	excerpt,
	index,
	imageUrl,
	page,
	type,
	onSourceSelected,
	disabled,
	isMarkdownSectionSelected,
	analyticsEventDomain = "Chat",
}: MarkdownInlineSourceProps) {
	const { isDisconnected } = useDisconnectedState();

	// eslint-disable-next-line complexity
	function handleCheckVisualisator() {
		const isWebSource = content?.source === "web";
		const fileTypeAccepted =
			content?.mimeType &&
			VISIONEUSE_ACCEPTED_CONTENT_MIME_TYPES.includes(content.mimeType) &&
			isValidContentMimeType(content.mimeType, false);

		if (!isWebSource && ((fileTypeAccepted && content) ?? messageId)) {
			onSourceSelected?.({
				fromUpload: content?.source === "upload",
				sourceFile: content?.url ?? "",
				excerpt,
				filename: content?.title ?? "",
				contentId: content?.id ?? "",
				messageId,
				source: content?.source ?? "",
				type: type ?? "text",
				isExcel: isSheetFile(content?.mimeType ?? ""),
				mimeType: content?.mimeType ?? "",
				vectorId,
				page,
				isImage: Boolean(imageUrl),
			});

			return;
		}

		window.open(
			contentId === "linkup" || contentId === "perplexity" ? excerpt : content?.url,
			"_blank",
		);
	}

	if (isLoading) {
		return (
			<Span className="relative -top-[1.5px] inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-on-primary transition-colors">
				<Icon name="spinner" />
			</Span>
		);
	}

	if (!content && !messageId && contentId !== "linkup" && contentId !== "perplexity") {
		return null;
	}

	return (
		<Span className="vb-source relative inline-block">
			<Button
				disabled={isDisconnected ?? disabled}
				className={cn(
					"mx-0.5 flex h-4 w-fit shrink-0 items-center rounded-full px-0.5",
					isMarkdownSectionSelected && "border border-primary-border",
				)}
				size="xs"
				classNames={{
					base: "px-0",
					text: "text-clip",
				}}
				tooltip={{
					className: "min-w-84 max-w-84",
					defaultPlace: "bottom",
					children: (
						<MarkdownInlineSourceTooltip
							contentId={contentId}
							content={content}
							excerpt={excerpt}
							index={index}
							imageUrl={imageUrl}
						/>
					),
				}}
				onClick={() => {
					ApiCaller.track([analyticsEventDomain, "Inline-source", "Opened"]);
					handleCheckVisualisator();
				}}
			>
				{index}
			</Button>
		</Span>
	);
}
